// ** React Imports
import { useContext, useState } from 'react'

// ** MUI Imports
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'

// ** Next Imports
import NextLink from 'next/link'
import { useRouter } from 'next/router'

// ** Icons Imports
import ExpandCircleDownRoundedIcon from '@mui/icons-material/ExpandCircleDownRounded'

// ** Hook Imports
import useSWR from 'swr'
import useFetch from 'src/hooks/useFetch'
import usePegawaiInternal from 'src/hooks/data/usePegawaiInternal'
import usePelamarIdentitas from 'src/hooks/data/usePelamarIdentitas'
import useRekrutmenDetail from 'src/hooks/data/useRekrutmenDetail'

// ** Context
import { AuthContext } from 'src/context/AuthContext'

// ** Configs
import endPoint from 'src/configs/endpoints/riwayatHidupEndPoints'
import endPointRekrutmen from 'src/configs/endpoints/rekrutmenEndPoints'

// ** Custom Imports
import SpinnerSmall from 'src/components/SpinnerSmall'
import CustomModalJoin from './CustomModalJoin'
import CustomModalInternal from './CustomModalInternal'

import RekrutmenClosed from 'src/components/rekrutmen/detail/RekrutmenClosed'

interface response {
  success: boolean
  message: string
  data: {
    id: string
    user_id: string
    kelengkapan_data_wajib: number
    kelengkapan_data_tambahan: number
    data_wajib_kosong: string[]
    data_tambahan_kosong: string[]
  }
}

interface ResponseLamaran {
  success: boolean
  message: string
  data: {
    id: number
    judul: string
    sub_judul: string
    status_rekrutmen: string
    progres_tahapan_pelamar: number
  }[]
}

const CustomLihatRekrutmen = () => {
  // ** Hooks
  const router = useRouter()
  const { id } = router.query

  const { user } = useContext(AuthContext)
  const { dataPelamarIdentitas } = usePelamarIdentitas(user?.id)
  const { dataRekrutmenDetail, isLoadingRekrutmenDetail } = useRekrutmenDetail(Array.isArray(id) ? id[0] : id)

  const theme = useTheme()
  const hiddenMd = useMediaQuery(theme.breakpoints.down('md'))
  const hiddenSm = useMediaQuery(theme.breakpoints.down('sm'))
  const { FetchWithPayload } = useFetch()

  const [openModal, setOpenModal] = useState(false)

  const payload = {
    url: `${endPoint.kelengkapanRiwayatEndpoint}/${user?.id}`,
    method: 'GET'
  }
  const { data, isLoading } = useSWR<response>(
    [`${process.env.NEXT_PUBLIC_BASE_PATH}/api/reqHandler/`, payload],
    FetchWithPayload
  )

  const payloadLamaran = {
    url: `${endPointRekrutmen.rekrutmenJoinedEndpoint}/${user?.id}`,
    method: 'GET'
  }
  const { data: dataLamaran, isLoading: isLoadingLamaran } = useSWR<ResponseLamaran>(
    [`${process.env.NEXT_PUBLIC_BASE_PATH}/api/reqHandler/`, payloadLamaran],
    FetchWithPayload
  )

  const alreadyJoined = dataLamaran?.data?.some(ele => ele.id === parseInt(id as string, 10))

  const { dataPegawaiInternal } = usePegawaiInternal(undefined, !dataPelamarIdentitas)
  const pegawaiInternalFound = dataPegawaiInternal?.find(item => item.nik === dataPelamarIdentitas?.nik)

  return dataRekrutmenDetail &&
    !isLoadingRekrutmenDetail &&
    dataRekrutmenDetail?.tanggal_akhir_registrasi &&
    new Date(`${dataRekrutmenDetail?.tanggal_akhir_registrasi}T23:59:59`).getTime() > Date.now() ? (
    <Grid container>
      {!hiddenSm ? (
        <Grid
          item
          xs={12}
          sm={6}
          sx={{
            borderTopLeftRadius: 10,
            borderBottomLeftRadius: 10,
            backgroundColor: theme => theme.palette.secondary.dark
          }}
        >
          <Typography
            variant={!hiddenMd ? 'h3' : 'h4'}
            fontWeight={600}
            color={theme => theme.palette.primary.main}
            sx={{
              p: !hiddenMd ? 16 : 8,
              py: 16
            }}
          >
            {alreadyJoined ? 'Anda Sudah Mengikuti Rekrutmen Ini' : 'Tertarik? Registrasi Sekarang Juga'}
          </Typography>
        </Grid>
      ) : null}
      <Grid
        item
        xs={12}
        sm={6}
        sx={{
          borderRadius: hiddenSm ? 10 : 0,
          borderTopRightRadius: 10,
          borderBottomRightRadius: 10,
          backgroundColor: theme => theme.palette.primary.main,
          '&:hover': {
            transform: 'scale(1.005)',
            svg: {
              transform: 'scale(1.1) rotate(270deg)'
            }
          }
        }}
      >
        {user ? (
          !isLoading && data && !isLoadingLamaran && dataLamaran ? (
            data.data.kelengkapan_data_wajib == 100 ? (
              <Button
                variant='contained'
                onClick={() => (alreadyJoined ? router.push('/pelamar/lamaran-saya/') : setOpenModal(true))}
                sx={{ height: '100%', width: '100%', py: 16 }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Typography
                    variant={!hiddenMd ? 'h3' : 'h4'}
                    textAlign='center'
                    fontWeight={600}
                    color={theme => theme.palette.common.white}
                  >
                    {alreadyJoined ? 'Cek Lamaran Anda' : 'Ikuti Rekrutmen'}
                  </Typography>
                  <ExpandCircleDownRoundedIcon sx={{ fontSize: '3rem', color: 'white', transform: 'rotate(270deg)' }} />
                </Box>
              </Button>
            ) : (
              <Button variant='contained' disabled sx={{ height: '100%', width: '100%', py: 16 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Typography
                    variant={'h4'}
                    textAlign='center'
                    fontWeight={600}
                    color={theme => theme.palette.common.white}
                    sx={{}}
                  >
                    {`Riwayat Hidup Belum Lengkap (${Math.round(data.data.kelengkapan_data_wajib)}%)`}
                  </Typography>
                  <Typography
                    variant='caption'
                    textAlign='center'
                    fontWeight={500}
                    color={theme => theme.palette.common.white}
                    sx={{}}
                  >
                    Lengkapi Data Berikut:
                    <br />
                    {data.data.data_wajib_kosong.map((ele, index) =>
                      index < data.data.data_wajib_kosong.length - 1 ? ` ${ele},` : ` ${ele}`
                    )}
                  </Typography>
                </Box>
              </Button>
            )
          ) : (
            <SpinnerSmall />
          )
        ) : (
          <NextLink href={`/login`}>
            <Button variant='contained' sx={{ height: '100%', width: '100%', py: 16 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography
                  variant={!hiddenMd ? 'h3' : 'h4'}
                  textAlign='center'
                  fontWeight={600}
                  color={theme => theme.palette.common.white}
                >
                  Login Akun
                </Typography>
                <ExpandCircleDownRoundedIcon sx={{ fontSize: '3rem', color: 'white', transform: 'rotate(270deg)' }} />
              </Box>
            </Button>
          </NextLink>
        )}
      </Grid>

      {/* {user && <CustomModalJoin open={openModal} setOpen={setOpenModal} />} */}

      {/* {user && dataRekrutmenDetail?.rekrutmen_klasifikasi_id === '5' ? (
        pegawaiInternalFound && <CustomModalInternal open={openModal} setOpen={setOpenModal} />
      ) : (
        <CustomModalJoin open={openModal} setOpen={setOpenModal} />
      )} */}

      {user && Number(dataRekrutmenDetail?.rekrutmen_klasifikasi_id) === 5 ? (
        pegawaiInternalFound ? (
          <CustomModalJoin open={openModal} setOpen={setOpenModal} />
        ) : (
          <CustomModalInternal open={openModal} setOpen={setOpenModal} />
        )
      ) : (
        <CustomModalJoin open={openModal} setOpen={setOpenModal} />
      )}
    </Grid>
  ) : (
    <RekrutmenClosed />
  )
}
export default CustomLihatRekrutmen
